import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";
import { useSession } from "../useSession";

type Waiting = {
  id: string;
  patient_id: string;
  patient_name: string;
  category: "lab_report" | "imaging_report";
  title: string;
  arrived_at: string;
  waiting_hours: number;
  worst_interpretation: "normal" | "high" | "low" | "unknown";
};

const CATEGORY_LABELS: Record<string, string> = {
  lab_report: "Lab report",
  imaging_report: "Imaging",
};

const READING_LABELS: Record<string, string> = {
  high: "Above range",
  low: "Below range",
  normal: "Within range",
  // Not the same as normal, and the wording has to keep them apart: nothing
  // read this value against anything (ADR-017 point 6).
  unknown: "No range recorded",
};

function waited(hours: number) {
  if (hours < 1) return "under an hour";
  if (hours < 48) return `${Math.round(hours)} hours`;
  return `${Math.round(hours / 24)} days`;
}

/**
 * How loudly to draw the age.
 *
 * The complaint this screen exists to answer is that a result waiting 29 days
 * looked almost identical to one waiting 2 days: the ordering carried all the
 * urgency and nothing else did. Two steps rather than a gradient, because
 * these are visual emphasis and not clinical thresholds, and a scale with five
 * colours on it starts to read as a severity the data does not carry.
 */
function ageBand(hours: number) {
  if (hours >= 24 * 14) return "old";
  if (hours >= 24 * 3) return "waiting";
  return "fresh";
}

/**
 * Results nobody has read yet.
 *
 * This is the screen that exists because of what goes wrong without it: the
 * report is filed, the value is on the chart, and the abnormal figure on line
 * four reaches nobody in time.
 *
 * Nothing ages out. A result that has waited three weeks sits at the top, and
 * there is no control on this page that makes an item disappear without a
 * doctor saying they have read it.
 *
 * Only a doctor may acknowledge (CLN-F-101). A nurse sees the same queue,
 * because knowing what is waiting is how they know to chase somebody, and gets
 * no button that clears it.
 */
export function ResultsWaiting() {
  const { clinic } = useSession();
  const [rows, setRows] = useState<Waiting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const isDoctor = clinic?.role === "doctor";

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/results/unacknowledged", {
      params: { header: clinicHeader() },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Waiting[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const acknowledge = async (row: Waiting) => {
    setBusy(row.id);
    const { error: failed } = await api.POST("/portal/results/{id}/acknowledge", {
      params: { path: { id: row.id }, header: clinicHeader() },
      body: {},
    });
    setBusy(null);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    await load();
  };

  if (loading) return <p className="empty">Reading the queue…</p>;

  return (
    <section>
      <header className="screen-band">
        <div className="screen-band-photo" aria-hidden="true" />
        <div className="screen-band-words">
          <h1>Results waiting</h1>
        {/* The order is the server's and it is not plain age: out of range
            first, then anything with no range recorded, then the rest, oldest
            first inside each band (CLN-F-109). The page used to claim "oldest
            first", so a 26-day result above a 31-day one read as a sorting
            bug and invited somebody to go looking for one. */}
        <p className="screen-lead">
          Every result no doctor has read. Out of range first, then results with
          no range recorded, then the rest — oldest first within each. Nothing
            leaves this list on its own.
          </p>
        </div>
      </header>

      <ErrorNote message={error} />

      {rows.length === 0 && (
        <p className="empty">Nothing is waiting. Every result here has been read.</p>
      )}

      <ul className="res-list">
        {rows.map((row) => {
          const abnormal =
            row.worst_interpretation === "high" || row.worst_interpretation === "low";
          // Unknown is not normal. A value nothing was read against must never
          // be drawn as though somebody checked it (ADR-017 point 6).
          const unknown = row.worst_interpretation === "unknown";
          return (
            <li key={row.id} className={`res-row${abnormal ? " abnormal" : ""}`}>
              {/* Age first, and at a size that scans down the column without
                  being read. */}
              <div className={`res-age age-${ageBand(row.waiting_hours)}`}>
                <span className="res-age-value">{waited(row.waiting_hours)}</span>
                <span className="res-label">waiting</span>
              </div>

              <div className="res-who">
                <Link to={`/patients/${row.patient_id}`} className="res-name">
                  {row.patient_name}
                </Link>
                <span className="res-kind">
                  {CATEGORY_LABELS[row.category] ?? row.category}
                </span>
              </div>

              <div className="res-range">
                <span
                  className={
                    abnormal
                      ? "res-reading out"
                      : unknown
                        ? "res-reading unknown"
                        : "res-reading"
                  }
                >
                  {READING_LABELS[row.worst_interpretation]}
                </span>
              </div>

              {isDoctor ? (
                <button
                  type="button"
                  disabled={busy === row.id}
                  onClick={() => acknowledge(row)}
                >
                  {busy === row.id ? "Recording…" : "I have read this"}
                </button>
              ) : (
                <span className="quiet res-not-yours">A doctor has to read this</span>
              )}
            </li>
          );
        })}
      </ul>

    </section>
  );
}
